import { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import DocumentTemplateForm from "../DocumentTemplateForm/DocumentTemplateForm";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import useGetDocumentTemplate from "./api/useGetDocumentTemplate";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import useOauth from "@utils/customHooks/useOauth";
import { AdminFormPage, type PageTab } from "@components/CatalogIndex";
import VersionHistory from "@components/VersionHistory";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";

type Tab = "template" | "versionHistory";

const FORM_ID = "document-template-form";

function Edit({ account, permission }) {
  const [authorizationCode] = useOauth();
  const [activeTab, setActiveTab] = useState<Tab>("template");
  const { hasPrivilege } = usePrivilegeCheck();
  const canViewAuditLogs = hasPrivilege("settings.audit_logs", [
    "view",
    "manage",
  ]);
  const canManageAuditLogs = hasPrivilege("settings.audit_logs", ["manage"]);

  const tabs: PageTab[] = [
    { key: "template", label: "Template" },
    ...(canViewAuditLogs
      ? [{ key: "versionHistory", label: "Version History" }]
      : []),
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const url = window.location.href;
  const id = url.split("/").slice(-1)?.[0] || "0";
  const { template, isLoading, isFetching, refetch } =
    useGetDocumentTemplate(id);
  const [errorMessages, setErrorMessages] = useState<string | string[]>(
    "Something went wrong! Your changes could not be saved at this time."
  );
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "Document Template saved successfully!"
  );
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const [initializing, setInitializing] = useState(
    location?.["state"]?.new ? true : false
  );

  useEffect(() => {
    if (initializing) {
      setSuccessMessage("Document Template created!");
      setShowSuccessAlert(true);
      setTimeout(() => {
        refetch()
          .unwrap()
          .then(() => {
            navigate(location.pathname, { replace: true, state: {} });
            setInitializing(false);
          });
      }, 2000);
    }
  }, [initializing]);

  const handleDownload = async () => {
    const response = await fetch(template?.downloadhref, {
      headers: { Authorization: `Bearer ${authorizationCode}` },
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download =
      template?.mergeTemplateFilename || template?.name || "download";
    a.click();

    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="editDocumentTemplatesPage">
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <AdminFormPage
        title={`Edit ${template?.name || ""}`}
        formId={permission !== "view" ? FORM_ID : undefined}
        onCancel={() => navigate(`/admin/document_templates`, { state: {} })}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as Tab)}
        extraActions={
          permission !== "view" && template?.downloadhref ? (
            <Button
              className="btnSeawhite"
              style={{ width: "155px" }}
              onClick={handleDownload}
            >
              Download Current
            </Button>
          ) : undefined
        }
      >
        {isLoading || isFetching || initializing ? (
          <SmallSpinner />
        ) : (
          <>
            <div
              style={
                activeTab === "versionHistory" ? { display: "none" } : undefined
              }
            >
              <DocumentTemplateForm
                formId={FORM_ID}
                documentTemplateProp={template}
                account={account}
                permission={permission}
                refetch={refetch}
              />
            </div>
            {activeTab === "versionHistory" && (
              <div className="admin-list-card--bare">
                <VersionHistory
                  filter={{
                    "context-type": "DocumentTemplate",
                    "context-id": id,
                  }}
                  permission={canManageAuditLogs}
                  account={account?.data}
                />
              </div>
            )}
          </>
        )}
      </AdminFormPage>
    </div>
  );
}

export default Edit;
